import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Dropdown } from 'semantic-ui-react';
import { isInternalURL } from '@plone/volto/helpers';
import { useSelector } from 'react-redux';

import NavItem from '@plone/volto/components/theme/Navigation/NavItem';

import './less/Navitems.less';

const NavItems = ({ items, lang, closeMenu, isMobile = false }) => {
  const [linkContents, setLinkContents] = useState({});
  const [externalUrls, setExternalUrls] = useState({});
  const [openDropdowns, setOpenDropdowns] = useState({});
  const isAuthenticated = useSelector((state) => state.userSession?.token);

  // Función para obtener la URL externa de un contenido Link
  const getExternalUrl = useCallback(async (url) => {
    if (linkContents[url]) {
      return linkContents[url];
    }

    try {
      const apiUrl = `/++api++${url}?expand=remoteUrl`;
      const response = await fetch(apiUrl);

      if (!response.ok) {
        console.error('API response not ok:', response.status, response.statusText);
        return null;
      }

      const data = await response.json();

      // Solo verificar remoteUrl si es un contenido Link
      if (data['@type'] === 'Link' && data.remoteUrl && data.remoteUrl.startsWith('http')) {
        setLinkContents(prev => ({ ...prev, [url]: data.remoteUrl }));
        setExternalUrls(prev => ({ ...prev, [url]: data.remoteUrl }));
        return data.remoteUrl;
      }
    } catch (error) {
      console.error('Error fetching link content:', error);
    }
    return null;
  }, [linkContents]);

  // Cargar URLs externas solo para contenidos Link
  useEffect(() => {
    const loadExternalUrls = async () => {
      const urlsToCheck = [];

      // Recopilar todas las URLs que necesitamos verificar
      items.forEach(item => {
        if (item.items && item.items.length > 0) {
          item.items.forEach(dropdownitem => {
            if (dropdownitem.url !== '' && dropdownitem.url !== '/') {
              urlsToCheck.push(dropdownitem.url);
            }
          });
        }
      });

      // Verificar cada URL solo si no está en caché
      for (const url of urlsToCheck) {
        if (!linkContents[url]) {
          try {
            const quickApiUrl = `/++api++${url}`;
            const response = await fetch(quickApiUrl);
            if (response.ok) {
              const data = await response.json();
              // Solo hacer la llamada completa si es un Link
              if (data['@type'] === 'Link') {
                await getExternalUrl(url);
              }
            }
          } catch (error) {
            console.error('Error checking content type:', error);
          }
        }
      }
    };

    loadExternalUrls();
  }, [items, linkContents, getExternalUrl]);

  const handleItemClick = useCallback((e, { name }) => {
    closeMenu();
    const dropdownmenu = document.getElementsByClassName(
      'hamburger hamburger--spin is-active',
    );
    if (dropdownmenu.length > 0) {
      dropdownmenu[0].classList.remove('is-active');
    }

    const dropdownmenuitems = document.getElementsByClassName('mobile-menu');
    if (dropdownmenuitems.length > 0) {
      dropdownmenuitems[0].classList.remove('mobile-menu-enter-done');
      dropdownmenuitems[0].classList.add(
        'mobile-menu-exit',
        'mobile-menu-exit-active',
      );
    }
  }, [closeMenu]);

  // Memoizar la función de renderizado de dropdown items
  const renderDropdownItem = useCallback((dropdownitem) => {
    const externalUrl = externalUrls[dropdownitem.url];
    const isInternal = isInternalURL(dropdownitem.url) || dropdownitem.url === '';
    const shouldOpenExternal = externalUrl && !isAuthenticated;
    const className = `me-3${
      dropdownitem.items && dropdownitem.items.length > 0 ? ' more-items' : ''
    }`;

    if (isInternal) {
      if (shouldOpenExternal) {
        return (
          <Dropdown.Item
            onClick={handleItemClick}
            value={dropdownitem.title}
            text={dropdownitem.title}
            key={dropdownitem.url}
            href={externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={className}
          />
        );
      } else {
        return (
          <Dropdown.Item
            onClick={handleItemClick}
            value={dropdownitem.title}
            text={dropdownitem.title}
            key={dropdownitem.url}
            as={Link}
            to={dropdownitem.url === '' ? '/' : dropdownitem.url}
            className={className}
          />
        );
      }
    } else {
      return (
        <Dropdown.Item
          onClick={handleItemClick}
          value={dropdownitem.title}
          text={dropdownitem.title}
          key={dropdownitem.url}
          href={dropdownitem.url}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
        />
      );
    }
  }, [externalUrls, isAuthenticated, handleItemClick]);

  // Memoizar la función de renderizado de sub-dropdown items
  const renderSubDropdownItem = useCallback((subdropdownitem) => {
    const isInternal = isInternalURL(subdropdownitem.url) || subdropdownitem.url === '';

    if (isInternal) {
      return (
        <Dropdown.Item
          onClick={handleItemClick}
          value={subdropdownitem.title}
          text={subdropdownitem.title}
          key={subdropdownitem.url}
          as={Link}
          to={subdropdownitem.url === '' ? '/' : subdropdownitem.url}
          className="me-0 ms-3"
        />
      );
    } else {
      return (
        <Dropdown.Item
          onClick={handleItemClick}
          value={subdropdownitem.title}
          text={subdropdownitem.title}
          key={subdropdownitem.url}
          href={subdropdownitem.url}
          target="_blank"
          rel="noopener noreferrer"
          className="me-0 ms-3"
        />
      );
    }
  }, [handleItemClick]);

  // Handler para gestionar el estado de los dropdowns
  const handleDropdownToggle = useCallback((index, isOpen, e) => {
    // Prevenir que se cierren otros dropdowns cuando se abre uno nuevo
    if (e) {
      e.stopPropagation();
    }
    setOpenDropdowns(prev => ({
      ...prev,
      [index]: isOpen
    }));
  }, []);

  // Memoizar los items procesados
  const processedItems = useMemo(() => {
    return items.map((item, index) =>
      item && item.items && item.items.length > 0 ? (
        <div key={item.url}>
          <label id={`dropdown-label-${index}`} className="visually-hidden">
            {item.title}
          </label>
          <Dropdown
            text={item.title}
            className="item me-3"
            labeled={true}
            aria-labelledby={`dropdown-label-${index}`}
            id={`dropdown-menu-${index}`}
            key={item.url}
            {...(isMobile ? {
              open: openDropdowns[index] || false,
              onOpen: (e) => handleDropdownToggle(index, true, e),
              onClose: (e) => handleDropdownToggle(index, false, e),
              closeOnBlur: false,
              closeOnDocumentClick: false,
            } : {})}
          >
            <Dropdown.Menu
              key={item.url}
              className={`rounded-4 py-2 overflow-hidden${
                index >= items.length - 5 ? ' left' : ''
              }`}
            >
              {item.items.map((dropdownitem) => (
                <React.Fragment key={dropdownitem.url}>
                  {renderDropdownItem(dropdownitem)}
                  {dropdownitem.items &&
                    dropdownitem.items.map(renderSubDropdownItem)}
                </React.Fragment>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      ) : (
        <NavItem
          item={item}
          lang={lang}
          key={item.url}
          onClick={isMobile && closeMenu ? closeMenu : undefined}
        />
      ),
    );
  }, [
    items,
    lang,
    renderDropdownItem,
    renderSubDropdownItem,
    openDropdowns,
    handleDropdownToggle,
    isMobile,
    closeMenu,
  ]);

  return <>{processedItems}</>;
};

export default NavItems;
